import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import * as _ from 'lodash';
import {ExtendedWrappedFieldProps} from '../FormFields';
import ActionsPanel, {ActionsPanelProps} from 'cl-layout/ActionsPanel';
import ReduxTable from 'cl-table/redux/ReduxTable';
import {VirtualizedComponentProps} from 'cl-table/consts';
import {ColumnsConfig, RowDataType, Selection, TableState} from 'cl-table/redux/actions';
import {TableFilterProps} from 'cl-table/TableFilter/TableFilter';
import createTableContext, {ReduxTableOptions, TableDataSource} from 'cl-table/redux/createTableContext';
import {convertSelectionToIDs} from '../../../shared/ActionsManager/VariationsActions/VariationsActionsHelpers';
import {createStyles, WithStyles, withStyles} from '@material-ui/core/styles';

export type FormTableProps<RowData extends RowDataType = RowDataType> = ExtendedWrappedFieldProps &
  ActionsPanelProps &
  WithStyles<typeof styles> &
  Pick<VirtualizedComponentProps<RowData>, 'thumbnail' | 'selectable' | 'noContentRenderer' | 'supportChildren'> &
  Pick<TableState<RowData>, 'data' | 'columns'> &
  Pick<ReduxTableOptions<RowData>, 'loadMoreRows'> & {
    tableName: string;
    config?: object;
    filterProps?: Partial<TableFilterProps>;
    updateRow: (columnName, rows, rowId, value) => object[];
    rows: object;
  };
const FormTable: React.FunctionComponent<FormTableProps> = ({
  tableName,
  config,
  supportChildren,
  thumbnail,
  selectable,
  filterProps,
  noContentRenderer,
  actions,
  data,
  columns,
  classes: {root},
  input: {onChange, value},
  updateRow,
  loadMoreRows,
}) => {
  const valueRef = useRef(null);

  const handleChange = async (selection: Selection, columnName: string, newValue) => {
    const rows = valueRef.current;
    let idsToUpdate = convertSelectionToIDs(selection, _.keys(rows));
    let updatedField = {};
    _.forEach(idsToUpdate, rowId => {
      updatedField = {...updatedField, ...updateRow(columnName, rows, rowId, newValue)};
    });
    updatedField = {...rows, ...updatedField}; // handles both add and update
    onChange(_.toArray(updatedField));
  };

  const [FormTableContext, setFormTableContext] = useState(null);

  useEffect(() => {
    setFormTableContext(() =>
      createTableContext<RowDataType>({
        name: tableName,
        getInitialState: () => {
          return {
            columnsConfig: new ColumnsConfig<RowDataType>(config),
            virtualized: false,
          };
        },
        onChange: handleChange,
        props: {
          supportChildren: supportChildren,
          thumbnail: thumbnail,
          selectable: selectable,
          filterProps: filterProps,
          noContentRenderer: noContentRenderer,
        },
        dataSource: loadMoreRows ? TableDataSource.INTERNAL : TableDataSource.EXTERNAL_LOADED,
        showAllVisibleColumns: true,
        loadMoreRows: loadMoreRows,
      })
    );
  }, []);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  if (!FormTableContext || (!noContentRenderer && data.size <= 0)) {
    return null;
  }
  return (
    <div className={root}>
      <FormTableContext data={data} columns={columns}>
        {actions ? <ActionsPanel sticky actions={actions} /> : undefined}
        <ReduxTable />
      </FormTableContext>
    </div>
  );
};

const styles = createStyles({
  root: {
    overflow: 'auto',
  },
});
export default withStyles(styles)(FormTable);
